import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useMediaQuery, IconButton, Button, TextField, InputAdornment, Tooltip, Divider, LinearProgress, Pagination, Stack, Typography } from '@mui/material';
import { PiBuildingsLight as OrganizationIcon } from "react-icons/pi";
import { BsPlusLg as Add } from 'react-icons/bs';
import Search from '@mui/icons-material/Search';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { IoClose as Close } from "react-icons/io5";
import ErrorHandler from '@Root/ErrorHandler';
import OrganizationCardForm from '@RegistriesForms/OrganizationCardForm';
import OrganizationCardRow from '@RegistriesRepresentationalDisplay/OrganizationCardRow';
import OrganizationCardDisplay from '@RegistriesFunctionalDisplay/OrganizationCardDisplay';
import OrganizationCardService from '@Helpers/URLHelpers/WeboutService/OrganizationCardService';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './OrganizationCardPage.scss';

export default function OrganizationCardPage(props) {
    const [showLoader, setShowLoader] = useState(false);
    const [showOrganizationCardForm, setShowOrganizationCardForm] = useState(false);
    const [showDetailPanel, setShowDetailPanel] = useState(true);
    const [organizationCardList, setOrganizationCardList] = useState([]);
    const [selectedOrganizationCard, setSelectedOrganizationCard] = useState();
    const [searchQuery, setSearchQuery] = useState('');
    const { currentProjectId } = useReduxProject();
    const defaultFilters = {
        organizationCardFilter: {
            projectIdList: [currentProjectId],
            sortBy: 'createTimeStamp',
            sortAsc: false
        },
        page: 0,
        size: 10
    };
    const [page, setPage] = useState(0);
    const [size] = useState(10);
    const [filters, setFilters] = useState(defaultFilters);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResult, setTotalResult] = useState(0);
    const errorHandler = new ErrorHandler();
    const organizationCardService = new OrganizationCardService();
    const isMobile = useMediaQuery('(max-width: 800px)');
    const className = 'registries__organizationCardPage';

    useEffect(() => {
        setShowLoader(true);
        renderData();
        const interval = setInterval(renderData, 20000);
        return () => clearInterval(interval);
    }, []);

    const renderData = async () => {
        try {
            let res = await organizationCardService.getListFiltered(filters);
            res = JSON.parse(res.body);
            setOrganizationCardList(res?.result?.organizationCardList || []);
            setTotalPages(res?.result?.totalPages || 1);
            setTotalResult(res?.result?.totalItems || 0);
        } catch (err) {
            errorHandler.handleError(err);
        }
        setShowLoader(false);
    };

    const reRenderData = async (newPage, newSize) => {
        try {
            setShowLoader(true);
            const f = { ...filters, page: newPage - 1, size: newSize };
            let res = await organizationCardService.getListFiltered(f);
            res = JSON.parse(res.body);
            setFilters(f);
            setOrganizationCardList(res?.result?.organizationCardList || []);
            setTotalPages(res?.result?.totalPages || 1);
            setTotalResult(res?.result?.totalItems || 0);
        } catch (err) {
            errorHandler.handleError(err);
        }
        setShowLoader(false);
    };

    const toggleForm = () => setShowOrganizationCardForm(v => !v);

    const handleSelect = (card) => {
        setSelectedOrganizationCard(card);
        props?.handleLabelData(card);
        if (isMobile) setShowDetailPanel(true);
    };

    const handleSearchQueryChange = (e) => {
        const value = DOMPurify.sanitize(e.target.value);
        setSearchQuery(value);
        const f = { ...filters };
        f.organizationCardFilter.searchQuery = value;
        setFilters(f);
    };

    const executeSearch = () => reRenderData(page + 1, size);

    const handleSearchKey = (e) => {
        if (e.key === 'Enter') executeSearch();
    };

    const clearSearch = () => {
        setSearchQuery('');
        setFilters(defaultFilters);
        setPage(0);
        renderData();
    };

    const handlePagination = (_, v) => {
        setPage(v);
        reRenderData(v, size);
    };

    const toggleDetailPanel = () => setShowDetailPanel(p => !p);

    return (
        <div className={className + ' height-wfa'}>
            <div className={`${className}__layout`}>
                {/* List Panel */}
                <div className={`${className}__list`}>
                    <div className={`${className}__list__header`}>
                        <div className={`${className}__list__title`}>
                            <OrganizationIcon className={`${className}__list__title__icon`} />
                            <div className={`${className}__list__title__text`}>
                                <Typography variant='h6'>Organization Cards</Typography>
                                <Typography variant='caption' sx={{ color: 'var(--color-primaryGrey)' }}>
                                    {totalResult} total items
                                </Typography>
                            </div>
                        </div>
                        <Button variant='contained' startIcon={<Add />} onClick={toggleForm} className={`${className}__list__createBtn`}>
                            Create
                        </Button>
                    </div>
                    <Divider />
                    <div className={`${className}__list__search`}>
                        <TextField
                            placeholder='Search organization cards...'
                            value={searchQuery}
                            onChange={handleSearchQueryChange}
                            onKeyDown={handleSearchKey}
                            size='small'
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <Search fontSize='small' />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position='end'>
                                        <Tooltip title='Clear search'>
                                            <IconButton size='small' onClick={clearSearch}>
                                                <FilterAltOffOutlinedIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                )
                            }}
                            className={`${className}__list__searchInput`}
                        />
                    </div>
                    <div className={`${className}__list__content`}>
                        {showLoader ? (
                            <div className={`${className}__list__loader`}>
                                <LinearProgress />
                            </div>
                        ) : organizationCardList.length === 0 ? (
                            <div className={`${className}__list__empty`}>
                                <OrganizationIcon className={`${className}__list__empty__icon`} />
                                <Typography variant='body1' sx={{ color: 'var(--color-primaryGrey)' }}>No organization cards found</Typography>
                                <Button variant='outlined' startIcon={<Add />} size='small' onClick={toggleForm}>
                                    Create first organization
                                </Button>
                            </div>
                        ) : (
                            <div className={`${className}__list__items`}>
                                {organizationCardList.map((card, idx) => (
                                    <OrganizationCardRow
                                        key={card?.id || idx}
                                        organizationCard={card}
                                        selectedOrganizationCard={selectedOrganizationCard}
                                        setSelectedOrganizationCard={handleSelect}
                                    />
                                ))}
                            </div>
                        )}
                        <div className={`${className}__list__add`} onClick={toggleForm}>
                            <Add className={`${className}__list__add__icon`} /> Create New Organization Card
                        </div>
                    </div>
                    {totalPages > 1 && (
                        <div className={`${className}__list__footer`}>
                            <Divider />
                            <div className={`${className}__list__footer__inner`}>
                                <Stack>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={handlePagination}
                                        showFirstButton
                                        showLastButton
                                        size={isMobile ? 'small' : 'medium'}
                                    />
                                </Stack>
                            </div>
                        </div>
                    )}
                </div>

                {/* Detail Panel */}
                <div
                    className={
                        `${className}__detail` +
                        (isMobile
                            ? showDetailPanel && selectedOrganizationCard ? ` ${className}__detail--open` : ` ${className}__detail--closed`
                            : '')
                    }
                >
                    {isMobile && selectedOrganizationCard && (
                        <IconButton
                            size='small'
                            className={`${className}__detail__closeBtn`}
                            onClick={toggleDetailPanel}
                        >
                            <Close />
                        </IconButton>
                    )}
                    {selectedOrganizationCard && (!isMobile || showDetailPanel) ? (
                        <OrganizationCardDisplay
                            organizationCard={selectedOrganizationCard}
                            reloadData={renderData}
                            handleLabelAnalytics={props?.handleLabelAnalytics}
                        />
                    ) : (
                        !isMobile && (
                            <div className={`${className}__detail__empty`}>
                                <OrganizationIcon className={`${className}__detail__empty__icon`} />
                                <Typography variant='h6' sx={{ color: 'var(--color-primaryGrey)' }} gutterBottom>
                                    No Selection
                                </Typography>
                                <Typography variant='body2' sx={{ color: 'var(--color-primaryGrey)' }}>
                                    Select an organization card from the list to view details
                                </Typography>
                            </div>
                        )
                    )}
                </div>
            </div>

            {showOrganizationCardForm && (
                <OrganizationCardForm
                    open={showOrganizationCardForm}
                    closePopup={toggleForm}
                    reloadData={renderData}
                />
            )}
        </div>
    );
}
